import { IconFlowerFilled, IconSnowflake } from "@tabler/icons-react";
import { type CSSProperties, useMemo } from "react";

import { useMotion } from "@/app/use-motion";
import type { Season } from "@/shared/api/types";

export type SeasonFxSurface = "header" | "nav";

export type SeasonParticle = {
	id: number;
	left: number;
	delay: number;
	duration: number;
	size: number;
	drift: number;
	spin: number;
	bob: number;
	variant: 0 | 1 | 2;
	opacity: number;
};

export const SEASON_HEADER_PARTICLE_COUNT = 18;
export const SEASON_NAV_PARTICLE_COUNT = 14;

export function seasonParticles(
	kind: Season,
	options?: { count?: number; surface?: SeasonFxSurface },
): SeasonParticle[] {
	const winter = kind === "winter";
	const surface = options?.surface ?? "header";
	const count =
		options?.count ??
		(surface === "nav"
			? SEASON_NAV_PARTICLE_COUNT
			: SEASON_HEADER_PARTICLE_COUNT);
	const tall = surface === "nav";

	return Array.from({ length: count }, (_, i) => ({
		id: i,
		left: (i * 17 + 8) % 100,
		delay: (i * 0.47) % (winter ? 9 : 8),
		duration: tall
			? (winter ? 16 : 18) + (i % 6) * 2.2
			: (winter ? 7.2 : 8.4) + (i % 6) * 1.1,
		size: winter ? 8 + (i % 5) * 2 : 12 + (i % 4) * 3,
		drift: winter ? -16 + (i % 9) * 5 : 24 + (i % 8) * 7,
		spin: (i * 53) % 360,
		bob: (i % 5) * 7,
		variant: (i % 3) as 0 | 1 | 2,
		opacity: 0.42 + (i % 4) * 0.14,
	}));
}

type SeasonFxProps = {
	season: Season;
	surface: SeasonFxSurface;
};

export function SeasonFx({ season, surface }: SeasonFxProps) {
	const { headerFx, navFx } = useMotion();
	const enabled = surface === "header" ? headerFx : navFx;
	const particles = useMemo(
		() => seasonParticles(season, { surface }),
		[season, surface],
	);

	if (!enabled) {
		return null;
	}

	return (
		<div
			className="play-season-fx"
			data-kind={season}
			data-surface={surface}
			aria-hidden
		>
			{particles.map((particle) => (
				<span
					key={`${surface}-${season}-${particle.id}`}
					className="play-season-fx__item"
					data-variant={particle.variant}
					style={
						{
							left: `${particle.left}%`,
							width: particle.size,
							height: particle.size,
							animationDelay: `${particle.delay}s`,
							animationDuration: `${particle.duration}s`,
							"--p-drift": `${particle.drift}px`,
							"--p-spin": `${particle.spin}deg`,
							"--p-bob": `${particle.bob}px`,
							"--p-opacity": String(particle.opacity),
						} as CSSProperties
					}
				>
					{season === "winter" ? (
						<IconSnowflake size={particle.size} stroke={1.5} />
					) : (
						<IconFlowerFilled size={particle.size} />
					)}
				</span>
			))}
		</div>
	);
}
